// UltraFlex Admin — Banned List Management
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  ShieldAlert, Plus, Edit3, Trash2, Save, X, Search, Eye,
  Calendar, Mail, Phone, AlertTriangle, UserX, Check
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminBannedList = () => {
  const { token } = useAuth();
  const [persons, setPersons] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({ first_name: '', last_name: '', date_of_birth: '', email: '', phone: '', reason: '', notes: '' });
  const [error, setError] = useState(null);
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchBanned = useCallback(async () => {
    setLoading(true);
    try {
      const url = search
        ? `${API_URL}/api/banned?search=${encodeURIComponent(search)}`
        : `${API_URL}/api/banned`;
      const res = await fetch(url, { headers });
      const data = await res.json();
      setPersons(data.items || []);
      setTotal(data.total || 0);
    } catch { setPersons([]); }
    setLoading(false);
  }, [search, token]);

  useEffect(() => { fetchBanned(); }, [fetchBanned]);

  const resetForm = () => {
    setFormData({ first_name: '', last_name: '', date_of_birth: '', email: '', phone: '', reason: '', notes: '' });
    setEditingId(null); setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); setError(null);
    if (!formData.first_name || !formData.last_name || !formData.reason) {
      setError('Name and reason are required'); return;
    }
    try {
      const url = editingId ? `${API_URL}/api/banned/${editingId}` : `${API_URL}/api/banned`;
      const method = editingId ? 'PATCH' : 'POST';
      const res = await fetch(url, { method, headers, body: JSON.stringify(formData) });
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed');
      fetchBanned(); setShowForm(false); resetForm();
    } catch (err) { setError(err.message); }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Remove "${name}" from the banned list?`)) return;
    await fetch(`${API_URL}/api/banned/${id}`, { method: 'DELETE', headers });
    fetchBanned();
  };

  const startEdit = (p) => {
    setFormData({
      first_name: p.first_name, last_name: p.last_name,
      date_of_birth: p.date_of_birth || '', email: p.email || '',
      phone: p.phone || '', reason: p.reason, notes: p.notes || '',
    });
    setEditingId(p.id); setShowForm(true);
  };

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="admin-banned-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-uf-text" data-testid="banned-page-title">Banned List</h1>
          <p className="text-uf-text-muted text-sm mt-1">Manage banned individuals. New membership applications are auto-screened against this list.</p>
        </div>
        <button onClick={() => { resetForm(); setShowForm(!showForm); }} data-testid="add-banned-btn"
          className="flex items-center gap-2 bg-uf-red text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-uf-red/90">
          <Plus className="w-4 h-4" />{showForm && !editingId ? 'Cancel' : 'Add Person'}
        </button>
      </div>

      {/* Form */}
      {showForm && (
        <form onSubmit={handleSubmit} className="bg-uf-surface rounded-xl border border-uf-border p-5 space-y-4" data-testid="banned-form">
          <h3 className="text-sm font-semibold text-uf-text">{editingId ? 'Edit Banned Person' : 'Add to Banned List'}</h3>
          {error && <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-red-400 text-sm flex items-center gap-2"><AlertTriangle className="w-4 h-4" />{error}</div>}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div><label className="text-uf-text-muted text-xs block mb-1">First Name *</label>
              <input value={formData.first_name} onChange={e => setFormData({ ...formData, first_name: e.target.value })}
                className="w-full bg-uf-surface-hi border border-uf-border rounded-lg px-3 py-2 text-uf-text text-sm" data-testid="banned-first-name" required /></div>
            <div><label className="text-uf-text-muted text-xs block mb-1">Last Name *</label>
              <input value={formData.last_name} onChange={e => setFormData({ ...formData, last_name: e.target.value })}
                className="w-full bg-uf-surface-hi border border-uf-border rounded-lg px-3 py-2 text-uf-text text-sm" data-testid="banned-last-name" required /></div>
            <div><label className="text-uf-text-muted text-xs block mb-1">Date of Birth</label>
              <input type="date" value={formData.date_of_birth} onChange={e => setFormData({ ...formData, date_of_birth: e.target.value })}
                className="w-full bg-uf-surface-hi border border-uf-border rounded-lg px-3 py-2 text-uf-text text-sm" data-testid="banned-dob" /></div>
            <div><label className="text-uf-text-muted text-xs block mb-1">Email</label>
              <input type="email" value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })}
                className="w-full bg-uf-surface-hi border border-uf-border rounded-lg px-3 py-2 text-uf-text text-sm" data-testid="banned-email" /></div>
            <div><label className="text-uf-text-muted text-xs block mb-1">Phone</label>
              <input value={formData.phone} onChange={e => setFormData({ ...formData, phone: e.target.value })}
                className="w-full bg-uf-surface-hi border border-uf-border rounded-lg px-3 py-2 text-uf-text text-sm" data-testid="banned-phone" /></div>
          </div>
          <div><label className="text-uf-text-muted text-xs block mb-1">Reason for Ban *</label>
            <textarea value={formData.reason} onChange={e => setFormData({ ...formData, reason: e.target.value })} rows={2}
              className="w-full bg-uf-surface-hi border border-uf-border rounded-lg px-3 py-2 text-uf-text text-sm resize-none" data-testid="banned-reason" required /></div>
          <div><label className="text-uf-text-muted text-xs block mb-1">Notes</label>
            <textarea value={formData.notes} onChange={e => setFormData({ ...formData, notes: e.target.value })} rows={2}
              className="w-full bg-uf-surface-hi border border-uf-border rounded-lg px-3 py-2 text-uf-text text-sm resize-none" data-testid="banned-notes" /></div>
          <div className="flex gap-2 justify-end">
            <button type="button" onClick={() => { setShowForm(false); resetForm(); }} className="px-4 py-2 text-uf-text-muted text-sm">Cancel</button>
            <button type="submit" data-testid="submit-banned-btn" className="flex items-center gap-2 bg-uf-red text-white px-5 py-2 rounded-lg text-sm font-semibold">
              <Save className="w-4 h-4" />{editingId ? 'Update' : 'Add to Banned List'}
            </button>
          </div>
        </form>
      )}

      {/* Search */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-uf-text-muted" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name or email..."
            className="w-full pl-10 pr-4 py-2 bg-uf-surface border border-uf-border rounded-lg text-uf-text text-sm" data-testid="banned-search" />
        </div>
        <span className="text-uf-text-muted text-xs">{total} person{total !== 1 ? 's' : ''} on list</span>
      </div>

      {/* List */}
      {loading ? (
        <div className="bg-uf-surface rounded-xl border border-uf-border p-12 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-uf-red mx-auto" />
        </div>
      ) : persons.length === 0 ? (
        <div className="bg-uf-surface rounded-xl border border-uf-border p-12 text-center" data-testid="no-banned">
          <UserX className="w-12 h-12 text-uf-text-muted mx-auto mb-3 opacity-40" />
          <p className="text-uf-text-muted text-sm">No banned persons found.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="banned-list">
          {persons.map(p => (
            <div key={p.id} data-testid={`banned-card-${p.id}`}
              className="bg-uf-surface rounded-xl border border-uf-border p-5 flex items-start gap-4">
              <div className="w-10 h-10 bg-red-500/15 rounded-lg flex items-center justify-center flex-shrink-0">
                <ShieldAlert className="w-5 h-5 text-red-400" />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="text-uf-text font-semibold text-sm">{p.first_name} {p.last_name}</h4>
                <div className="flex flex-wrap items-center gap-3 mt-1 text-uf-text-muted text-xs">
                  {p.date_of_birth && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />DOB: {p.date_of_birth}</span>}
                  {p.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{p.email}</span>}
                  {p.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{p.phone}</span>}
                </div>
                <p className="text-uf-text-muted text-xs mt-1.5"><span className="text-red-400 font-medium">Reason:</span> {p.reason}</p>
                {p.notes && <p className="text-uf-text-muted text-xs mt-1 italic">{p.notes}</p>}
                <p className="text-uf-text-muted text-[10px] mt-2">Banned: {p.banned_at ? new Date(p.banned_at).toLocaleDateString('en-GB') : 'N/A'}</p>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => startEdit(p)} data-testid={`edit-banned-${p.id}`}
                  className="p-2 text-uf-text-muted hover:text-uf-text rounded-lg hover:bg-uf-surface-hi"><Edit3 className="w-4 h-4" /></button>
                <button onClick={() => handleDelete(p.id, `${p.first_name} ${p.last_name}`)} data-testid={`delete-banned-${p.id}`}
                  className="p-2 text-red-400/60 hover:text-red-400 rounded-lg hover:bg-red-500/10"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminBannedList;
